import { Component, OnInit, Inject, Injector } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BaseComponent } from 'src/app/common/base/base.component';
import { ResourceType } from 'src/app/common/enumerations/enumerations';
import { Resource } from 'src/app/components/catalogs/beamline-catalog/resource/resources';
import { Restriction } from 'src/app/controls/restrictions/services/restriction';


@Component({
  selector: 'app-restriction',
  templateUrl: './restriction.component.html',
  styleUrls: ['./restriction.component.scss']
})
export class RestrictionComponent extends BaseComponent implements OnInit {

  public appliesToList!: Resource[];
  public branchline!: Resource[];
  public acceleratorList!: Resource[];

  public appliesToSend?: Resource;

  public appliesToSelected!: number;
  public restrictionLanguage?: string;

  public isSaving!: boolean;

  constructor(
    protected override injector: Injector,
    public dialogRef: MatDialogRef<RestrictionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      restriction: Restriction,
      branchlines: Resource[],
      shutter: Resource,
      accelerators: Resource[]
    },

  ) {
    super(injector);

  }

  /**
   * Builds the restriction targets and loads every catalog shutter for selection.
   */
  ngOnInit(): void {
    this.clear();
    let idAux = -1;
    if (this.data.shutter && this.data.shutter.name) {
      if (this.data.shutter.id) {
        this.appliesToList.push(this.data.shutter);
      } else {
        this.data.shutter.id = idAux--;
        this.appliesToList.push(this.data.shutter);
      }
    }
    if (this.data.branchlines) {
      this.branchline = this.data.branchlines;
      this.branchline.map(data => {
        if (data.id) {
          this.appliesToList.push(data);
        } else {
          data.id = idAux--;
          this.appliesToList.push(data);
        }
      });
    }
    if (this.data.accelerators) {
      this.acceleratorList = this.data.accelerators;
      this.acceleratorList.map(data => {
        this.appliesToList.push(data);
      });
    }
    // Use the existing Resources store to avoid another catalog request when opening the dialog.
    const shutters = this.store.selectSnapshot<Resource[]>(state => state.Resources.data)
      .filter(resource => resource.type === ResourceType.Shutter && !!resource.name?.trim())
      .sort((a, b) => this.utils.sortArrayAlphabeticallyWithComplexNumbers(a.name, b.name));
    shutters.forEach(shutter => {
      if (!this.appliesToList.some(resource => resource.id === shutter.id)) {
        this.appliesToList.push(shutter);
      }
    });
    const locationName = this.data.restriction?.location?.name ?? this.data.restriction?.locationName;
    const savedLocationID = this.data.restriction?.location?.id ?? this.data.restriction?.locationID;
    const selectedLocationID = savedLocationID && savedLocationID !== 0
      ? savedLocationID
      : this.appliesToList.find(resource => resource.name === locationName)?.id;
    this.deduplicateAppliesToList(selectedLocationID);
    if (selectedLocationID) {
      this.appliesToSelected = selectedLocationID;
      this.appliesToSend = this.appliesToList.find(resource => resource.id === selectedLocationID);
    }
    this.restrictionLanguage = this.data.restriction?.restrictionLanguage;
  }

  /**
   * Removes duplicate location labels while retaining the resource selected by an existing restriction.
   *
   * @param selectedLocationID Existing restriction location to preserve when duplicate names exist.
   */
  private deduplicateAppliesToList(selectedLocationID?: number): void {
    const uniqueLocations = new Map<string, Resource>();
    this.appliesToList.forEach(resource => {
      const key = resource.name?.trim().toLowerCase();
      if (!key) return;
      const existing = uniqueLocations.get(key);
      if (!existing || resource.id === selectedLocationID) {
        uniqueLocations.set(key, resource);
      }
    });
    // Display each named location once while preserving its original catalog ordering.
    this.appliesToList = Array.from(uniqueLocations.values());
  }

  /**
   * Saves the restriction with both its selected resource and stable resource ID.
   */
  add() {
    this.isSaving = true;
    if (this.isValid()) {
      this.data.restriction.restrictionLanguage = this.restrictionLanguage;
      this.appliesToList.map(x => {
        if (x.id < 0) {
          x.id = 0;
        }
      });
      this.data.restriction.location = this.appliesToSend;
      // Preserve saved resource IDs so pending catalog responses can restore the Applies To selection.
      this.data.restriction.locationID = this.appliesToSend?.id && this.appliesToSend.id > 0 ? this.appliesToSend.id : 0;
      this.data.restriction.locationName = this.appliesToSend?.name ?? undefined;
      this.data.restriction.resourceID = 0;
      this.dialogRef.close(this.data.restriction);
    } else {
      this.isSaving = false;
    }
  }

  cancel() {
    this.dialogRef.close();
  }

  isValid() {
    if (!this.restrictionLanguage) {
      this.alert.message('restrictionDialog_Language_Required');
      return false;
    }
    if (!this.appliesToSelected) {
      this.alert.message('restrictionDialog_AppliesTo_Required');
      return false;
    }
    return true;
  }

  getInfoAppliesTo(appliesToSelectedId: number) {
    this.appliesToSend = this.appliesToList.find(x => x.id === appliesToSelectedId);
  }

  clear() {
    this.appliesToList = [];

    this.appliesToSend = new Resource();

    this.appliesToSelected = 0;
    this.restrictionLanguage = '';

    this.appliesToList.map(x => {
      if (x.id < 0) { x.id = 0; }
    });
  }

}
